import type { Unit } from '@/api/pending'

export type AudienceMode = 'all' | 'towers' | 'floors' | 'units'

export interface AudienceSelection {
  mode: AudienceMode
  towers: string[]
  floors: number[]
  unitIds: number[]
}

export const EMPTY_AUDIENCE: AudienceSelection = {
  mode: 'all',
  towers: [],
  floors: [],
  unitIds: [],
}

export const towerNames = (units: Unit[]): string[] =>
  [...new Set(units.map((u) => u.tower))].sort()

export const floorsOf = (units: Unit[]): number[] =>
  [...new Set(units.map((u) => u.floor ?? null))]
    .filter((f): f is number => f !== null)
    .sort((a, b) => a - b)

/** Flat unit ids for the selection; `null` means the whole condominium (field omitted). */
export function resolveAudience(sel: AudienceSelection, units: Unit[]): number[] | null {
  switch (sel.mode) {
    case 'all':
      return null
    case 'towers':
      return units.filter((u) => sel.towers.includes(u.tower)).map((u) => u.id)
    case 'floors':
      return units.filter((u) => u.floor != null && sel.floors.includes(u.floor)).map((u) => u.id)
    case 'units':
      return units.filter((u) => sel.unitIds.includes(u.id)).map((u) => u.id)
  }
}

export function audienceLabel(sel: AudienceSelection, units: Unit[]): string {
  const ids = resolveAudience(sel, units)
  if (ids === null) return `Todas as ${units.length} unidades`
  return ids.length === 0
    ? 'Nenhuma unidade selecionada'
    : `${ids.length} ${ids.length === 1 ? 'unidade' : 'unidades'}`
}

/** Poll end date (`YYYY-MM-DD`, local) to the API's `expires_in_hours`, counted to the end of that day. */
export function hoursUntilEndOf(date: string, now = new Date()): number {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number)
  const end = new Date(y, m - 1, d, 23, 59, 59)
  return Math.max(1, Math.ceil((end.getTime() - now.getTime()) / 36e5))
}

export function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Default poll end date: a week from now. */
export const defaultEndDate = (now = new Date()): string =>
  toDateInput(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7))
